import { useMemo, useRef, useState } from 'react';
import { IonItem, IonList, IonRadio, IonRadioGroup } from '@ionic/react';
import CheckedIcon from '../../assets/icons/CheckedIcon';
import ModalHeader from '../../components/modals/ModalHeader';
import ModalWrapper from '../../components/modals/ModalWrapper';
import useCompanySlug from '../../../../hooks/global/useCompanySlug';
import HeaderEndSlotBtn from '../../components/modals/HeaderEndSlotBtn';
import { useGetFranchiseDataQuery } from '../../../../store/api-slices';
import { IModal } from '../../router/ModalRouter';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import { CompanyDetailsInterface } from '../../../../services/exports/Interfaces';
import { useDefaultSlug } from '../../common/selectors/selectors';
import useCompanyHours from '../../../../hooks/availability/useCompanyHours';

const LocationModal = ({ goBack }: Partial<IModal>) => {
  const selectedCompanyRef = useRef<HTMLIonRadioGroupElement>();
  const { slug } = useDefaultSlug();
  const { data, isFetching } = useGetFranchiseDataQuery(true);
  const [loading, setLoading] = useState<boolean>(false);
  const { updateSlug } = useCompanySlug();
  const companies = useMemo<CompanyDetailsInterface[]>(
    () =>
      collect(isFetching ? [] : data.data.companies)
        .where('has_live_orders', true)
        .toArray(),
    [isFetching, data?.data?.companies],
  );

  const onDoneClick = async (): Promise<void> => {
    const newSelectedCompany = selectedCompanyRef.current?.value;

    if (!newSelectedCompany) {
      return;
    }

    setLoading(true);
    const success = await updateSlug(newSelectedCompany);
    setLoading(false);

    if (success) {
      goBack();
    }
  };

  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:Location:LocationModal' });

  return (
    <>
      <ModalWrapper className="mt-4">
        <ModalHeader
          title={t('title')}
          onBack={goBack}
          loading={loading}
          endSlot={
            <HeaderEndSlotBtn onClick={onDoneClick}>
              <span className="text-brand-primary text-base">{t('buttons.done')}</span>
            </HeaderEndSlotBtn>
          }
        />

        <IonList className="min-h-[30vh]">
          <IonRadioGroup value={slug} ref={selectedCompanyRef}>
            {companies?.map((company) => (
              <CompanyRow key={company?.slug} company={company} />
            ))}
          </IonRadioGroup>
        </IonList>
      </ModalWrapper>
    </>
  );
};

const CompanyRow = ({ company }: { company: CompanyDetailsInterface }) => {
  const { open: openNow } = useCompanyHours({ data: company, skipHolidays: true });
  const unselectable = !openNow && !company?.has_scheduled_orders;

  if (unselectable) {
    return (
      <IonItem disabled className="opacity-50">
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground">{company?.name}</h4>
          <p className="text-xs font-medium text-muted-foreground">{company?.address}</p>
        </div>
      </IonItem>
    );
  }

  return (
    <IonItem>
      <IonRadio className="peer [&::part(container)]:hidden" value={company?.slug}>
        <h4 className="text-sm font-semibold">{company?.name}</h4>
        <p className="text-xs font-medium text-muted-foreground">{company?.address}</p>
      </IonRadio>
      <span slot="end" className="hidden peer-aria-checked:block size-6" aria-hidden="true">
        <CheckedIcon className="stroke-brand-primary" />
      </span>
    </IonItem>
  );
};

export default LocationModal;
